/**
 * domTranslateGuard — makes React resilient to in-page translation tools
 * (Google Translate, the Chrome "Translate this page" feature, browser
 * translation extensions, etc.).
 *
 * The problem
 * -----------
 * Google Translate rewrites the page by wrapping every text node in nested
 * `<font>` elements and swapping the original text node out of the tree. React
 * still holds a reference to the *original* text node. On its next commit React
 * calls `parent.removeChild(textNode)` or `parent.insertBefore(node, ref)` — but
 * the node's real parent is now a `<font>`, not the parent React expects. The
 * browser throws:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * That exception unwinds through React and trips our top-level
 * AppErrorBoundary, so the *entire* app shows "Something went wrong" — exactly
 * what a visitor sees the moment they let Chrome translate beetlesense.ai.
 *
 * The fix
 * -------
 * The canonical, low-risk mitigation (recommended on facebook/react#11538 and
 * shipped by many production React apps): wrap the two DOM methods so that a
 * parent/reference mismatch becomes a safe operation instead of a throw.
 * Correct DOM operations are completely unaffected — the guard only changes
 * behaviour for the exact mismatch case that translation tools create, where
 * the alternative is a hard crash.
 *
 * Must run before the first React render (imported first in index.tsx).
 */
export function installDomTranslateGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return

  const proto = Node.prototype as unknown as {
    __bsTranslateGuard?: boolean
    removeChild: <T extends Node>(child: T) => T
    insertBefore: <T extends Node>(node: T, ref: Node | null) => T
  }
  if (proto.__bsTranslateGuard) return
  proto.__bsTranslateGuard = true

  const originalRemoveChild = proto.removeChild
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // Translation tool already moved this node. Remove it from wherever it
      // actually lives so React's intent (detach it) still holds, then return.
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T
      }
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = proto.insertBefore
  proto.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was relocated by the translation tool; appending
      // keeps the new node in the tree instead of throwing.
      return originalInsertBefore.call(this, newNode, null) as T
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}
